import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-styles.css";
import { BarChart, barDataItem } from "react-gifted-charts";
import {
  Button,
  Text,
  XStack,
  YStack,
  Separator,
  getTokenValue,
  Token,
  Input,
} from "tamagui";

// Define interfaces for the sensor data
export interface SensorRollupData {
  sensorId: string;
  date: string;
  dateHour: string;
  hour: number;
  sensorType: string;
  hubId: string;
  totalDataPoints: number;
  createdAt: number;
  updatedAt: number;
}

// Define interface for the chart data
export interface SensorUptimeDataItem extends barDataItem {
  value: number;
  label: string;
  frontColor?: string;
  sensorId?: string;
  hour?: number;
  dateHour?: string;
}

interface SensorUptimeChartProps {
  sensorId: string | null;
  refreshData?: () => void;
  sensorData?: SensorRollupData[];
  isLoading?: boolean;
  error?: string | null;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  maxWidth?: number;
}

export const SensorUptimeChart: React.FC<SensorUptimeChartProps> = ({
  sensorId,
  refreshData,
  sensorData = [],
  isLoading = false,
  error = null,
  selectedDate: propsSelectedDate,
  onDateChange,
  maxWidth = 1800,
}) => {
  const [parentWidth, setParentWidth] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string>(
    propsSelectedDate || "",
  );
  const [selectedDateObj, setSelectedDateObj] = useState<Date | null>(
    propsSelectedDate ? new Date(propsSelectedDate) : null,
  );

  // Add window resize listener to update chart dimensions
  useEffect(() => {
    const handleResize = () => {
      // Force re-render to update chart dimensions
      setParentWidth(0);
      setTimeout(() => {
        // Allow the layout event to fire again
        setParentWidth((prevWidth) => (prevWidth === 0 ? 1 : 0));
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize with the current date
  useEffect(() => {
    const today = new Date();
    const formattedDate = formatDateString(today);
    setSelectedDate(formattedDate);
    setSelectedDateObj(today);
  }, []);

  // Update selected date string when date object changes
  useEffect(() => {
    if (selectedDateObj) {
      const formattedDate = formatDateString(selectedDateObj);
      setSelectedDate(formattedDate);
    }
  }, [selectedDateObj]);

  // Format a date object to YYYY-MM-DD string
  const formatDateString = (date: Date): string =>
    `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;

  // Process the sensor data for chart display
  const processChartData = (): SensorUptimeDataItem[] => {
    // Create an array of 24 hours with zero values as the base
    const hoursOfDay = Array.from({ length: 24 }, (_, hour) => ({
      value: 0,
      label: `${hour}:00`,
      frontColor: getTokenValue("$blue10" as Token, "color"),
      hour,
      dateHour: selectedDate
        ? `${selectedDate}-${hour.toString().padStart(2, "0")}`
        : "",
    }));

    // If we have no sensor data, return the zero-filled array
    if (!sensorData || sensorData.length === 0) {
      return hoursOfDay;
    }

    // Filter by selected date if one is chosen
    const filteredData = selectedDate
      ? sensorData.filter((item) => item.date === selectedDate)
      : sensorData;

    // Even if no data for the selected date, still return all 24 hours with zeros
    if (filteredData.length === 0) {
      return hoursOfDay;
    }

    // Create a map of hour -> data for faster lookup
    const hourDataMap = new Map();
    filteredData.forEach((item) => {
      hourDataMap.set(item.hour, {
        value: item.totalDataPoints,
        sensorId: item.sensorId,
        dateHour: item.dateHour,
      });
    });

    // Update our 24-hour array with actual data where available
    return hoursOfDay.map((hourItem) => {
      const actualData = hourDataMap.get(hourItem.hour);
      if (actualData) {
        return {
          ...hourItem,
          value: actualData.value,
          sensorId: actualData.sensorId,
          dateHour: actualData.dateHour,
          // Use a lighter color for hours with no data
          frontColor: getTokenValue("$blue10" as Token, "color"),
        };
      }
      // For hours with no data, use a lighter color
      return {
        ...hourItem,
        frontColor: getTokenValue("$blue4" as Token, "color"),
      };
    });
  };

  // Calculate chart dimensions based on parent width
  const rawWidth = parentWidth > 0 ? parentWidth - 80 : 300;
  // Apply maximum width constraint
  const chartWidth = Math.min(rawWidth, maxWidth - 80);
  const yAxisLabelWidth = 50; // Increased for larger number display

  // Always use 24 data points (one for each hour of the day)
  const dataPoints = 24;

  // Since we're always showing 24 bars, we use the full width up to the max
  const availableWidth = chartWidth - yAxisLabelWidth;

  // Calculate bar width and spacing based on the chart width
  const spacing = Math.max(5, (availableWidth * 0.2) / (dataPoints - 1));
  const barWidth = Math.max(15, (availableWidth * 0.8) / dataPoints);

  const data = processChartData();

  // Colors and styles
  const axisTextColor = getTokenValue("$textLight" as Token, "color");
  const rulesColor = getTokenValue("$white6" as Token, "color");

  const handleRefresh = () => {
    if (refreshData) {
      console.log("Refreshing data for SensorUptimeChartConnector");
      refreshData();
    }
  };

  return (
    <YStack
      backgroundColor="$backgroundHover"
      paddingHorizontal="$4"
      paddingVertical="$3"
      borderRadius="$4"
      space="$2"
      flex={1}
      maxWidth={maxWidth}
      onLayout={({ nativeEvent }) => setParentWidth(nativeEvent.layout.width)}
    >
      <XStack justifyContent="space-between" alignItems="center">
        <Text fontSize="$4" fontWeight="bold">
          Sensor Data Points
        </Text>
        <Button
          onPress={handleRefresh}
          size="$2"
          theme="blue"
          icon={<Text>↻</Text>}
        >
          Refresh
        </Button>
      </XStack>

      <XStack justifyContent="flex-end" alignItems="center" marginTop="$2">
        <Text fontSize="$2" marginRight="$2">
          Date:
        </Text>
        <YStack>
          <DatePicker
            selected={selectedDateObj}
            onChange={(date: Date | null) => {
              if (date) {
                setSelectedDateObj(date);
                const formattedDate = formatDateString(date);
                if (onDateChange) {
                  onDateChange(formattedDate);
                }
              }
            }}
            maxDate={new Date()} // Prevent selection of future dates
            dateFormat="yyyy-MM-dd"
            customInput={
              <XStack
                alignItems="center"
                width={150}
                borderWidth={1}
                borderColor="$borderColor"
                backgroundColor="$backgroundStrong"
                borderRadius="$2"
              >
                <Text paddingLeft="$2">📅</Text>
                <Input
                  flex={1}
                  value={selectedDate}
                  readOnly
                  size="$2"
                  textAlign="center"
                  cursor="pointer"
                  color="$color"
                  borderWidth={0}
                  backgroundColor="transparent"
                />
              </XStack>
            }
            popperPlacement="bottom-end"
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={10}
          />
        </YStack>
      </XStack>

      <Separator marginVertical="$2" />

      {isLoading && (
        <YStack padding="$4" alignItems="center">
          <Text>Loading data...</Text>
        </YStack>
      )}

      {error && (
        <YStack padding="$4" alignItems="center">
          <Text color="$red10">Error: {error}</Text>
        </YStack>
      )}

      {!sensorId ? (
        <Text padding="$4" textAlign="center">
          Select a sensor to view data point distribution
        </Text>
      ) : (
        <YStack flex={1} paddingVertical="$4" width="100%" alignItems="center">
          <Text
            fontSize="$2"
            marginBottom="$2"
            opacity={0.7}
            textAlign="center"
          >
            Hourly Data Points Distribution{" "}
            {selectedDate ? `for ${selectedDate}` : ""}
          </Text>
          <XStack space="$4" marginBottom="$2" justifyContent="center">
            <XStack space="$1" alignItems="center">
              <YStack
                width={12}
                height={12}
                backgroundColor="$blue10"
                borderRadius={2}
              />
              <Text fontSize="$1" opacity={0.8}>
                Data Points
              </Text>
            </XStack>
            <XStack space="$1" alignItems="center">
              <YStack
                width={12}
                height={12}
                backgroundColor="$blue4"
                borderRadius={2}
              />
              <Text fontSize="$1" opacity={0.8}>
                No Data
              </Text>
            </XStack>
          </XStack>

          {/* Wrapper to center the chart when it's narrower than container */}
          <XStack width="100%" justifyContent="center" alignItems="center">
            <BarChart
              data={data}
              barWidth={barWidth}
              spacing={spacing}
              noOfSections={5}
              barBorderRadius={4}
              hideRules={false}
              rulesColor={rulesColor}
              rulesThickness={1}
              showYAxisIndices={true}
              yAxisIndicesColor={rulesColor}
              yAxisTextStyle={{ color: axisTextColor }}
              yAxisLabelWidth={yAxisLabelWidth}
              height={300}
              width={chartWidth}
              yAxisThickness={1}
              xAxisThickness={1}
              yAxisColor={rulesColor}
              xAxisColor={rulesColor}
              yAxisLabelPrefix=""
              yAxisLabelSuffix=""
              disableScroll={false}
              showFractionalValues={false}
              maxValue={(() => {
                // Calculate max value with 10% headroom
                const maxDataValue = Math.max(
                  ...data.map((item) => item.value),
                );
                // If all values are zero, use 10 as default to show a proper y-axis
                return maxDataValue > 0 ? maxDataValue * 1.1 : 10;
              })()}
              renderTooltip={(item: SensorUptimeDataItem) => (
                <YStack
                  padding="$2"
                  backgroundColor="$backgroundStrong"
                  borderRadius="$2"
                  borderWidth={1}
                  borderColor="$borderColor"
                >
                  <Text fontSize="$1" fontWeight="bold">
                    {selectedDate || "Date"} {item.hour}:00
                  </Text>
                  <Text fontSize="$1">
                    Data Points: {item.value > 0 ? item.value : "No data"}
                  </Text>
                </YStack>
              )}
            />
          </XStack>
        </YStack>
      )}
    </YStack>
  );
};
